import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDataForSingleEventCaseName } from "../features/SingleEventCaseSlice";
import { useLocation } from "react-router-dom";
import Loading from "./Loading";
import MapContainer from "../components/map/MapContainer";
import { getSlideImageData } from "../features/MapSlice";
import { setCaseName, setEventName } from "../features/TabNameSlice";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";

const SIDEBAR_WIDTH = 420;

const SlideViewer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { data, loading } = useSelector((state) => state.singleCase);
  const { caseName } = useSelector((state) => state.tab);
  const isSmallScreen = useMediaQuery("(max-width:768px)");
  const searchParams = new URLSearchParams(location.search);
  const hideUI = searchParams.get("hideui") === "1";
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen && !hideUI);

  useEffect(() => {
    let pathArray = location.pathname.split("/");
    if (pathArray.length === 3) {
      dispatch(getDataForSingleEventCaseName({ eventName: pathArray[1], caseName: pathArray[2] }));
      dispatch(getSlideImageData({ eventName: pathArray[1], caseName: pathArray[2] }));
      dispatch(setEventName(pathArray[1]));
      dispatch(setCaseName(pathArray[2]));
    }
  }, [location.pathname, dispatch]);

  const caseInfoContent = data?.type === "md"
    ? data?.info
    : data?.info?.split("\n");

  const hasContent = caseInfoContent && (
    Array.isArray(caseInfoContent) ? caseInfoContent.length > 0 : caseInfoContent.length > 0
  );

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {loading ? (
        <Loading />
      ) : (
        <>
          {/* Slide Name bar with info toggle — hidden when hideui=1 */}
          {!hideUI && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#ffffff",
                px: 2,
                py: 0.75,
                borderBottom: "1px solid #e0e0e0",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 500,
                    color: "#616161",
                    fontSize: "0.825rem",
                  }}
                >
                  Slide Name:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: 500,
                    color: "#1d1d1f",
                    fontSize: "0.825rem",
                  }}
                >
                  {caseName ? decodeURIComponent(caseName) : caseName}
                </Typography>
              </Box>

              {hasContent && (
                <IconButton
                  size="small"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  title={sidebarOpen ? "Hide Case Info" : "Show Case Info"}
                  sx={{
                    color: sidebarOpen ? "#1718FF" : "#616161",
                    "&:hover": { backgroundColor: "rgba(23, 24, 255, 0.06)" },
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              )}
            </Box>
          )}

          {/* Main content: map + right sidebar */}
          <Box sx={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden", position: "relative" }}>
            {/* Map viewer */}
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <MapContainer />
            </Box>

            {/* Right sidebar — always rendered, animated via transform */}
            {hasContent && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: { xs: "100%", sm: SIDEBAR_WIDTH },
                  transform: sidebarOpen ? "translateX(0)" : { xs: "translateX(100%)", sm: `translateX(${SIDEBAR_WIDTH}px)` },
                  transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  borderLeft: "1px solid #e0e0e0",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#ffffff",
                  boxShadow: sidebarOpen ? "-4px 0 16px rgba(0, 0, 0, 0.06)" : "none",
                  zIndex: 10,
                }}
              >
                {/* Sidebar header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    borderBottom: "1px solid #e0e0e0",
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <InfoOutlinedIcon sx={{ color: "#1718FF", fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 500,
                        color: "#1d1d1f",
                        fontSize: "0.825rem",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Case Info
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setSidebarOpen(false)}>
                    <CloseIcon sx={{ fontSize: 18, color: "#616161" }} />
                  </IconButton>
                </Box>

                {/* Sidebar content */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: 2,
                    py: 2,
                    "& h1, & h2, & h3, & h4, & h5, & h6": {
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: "#1718FF",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mt: 2.5,
                      mb: 1,
                      "&:first-of-type": { mt: 0 },
                    },
                    "& p": {
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "#444",
                      lineHeight: 1.75,
                      my: 0.75,
                    },
                    "& ul, & ol": {
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      color: "#444",
                      lineHeight: 1.75,
                      pl: 2,
                    },
                    "& strong": {
                      fontWeight: 600,
                      color: "#1d1d1f",
                    },
                    "& blockquote": {
                      backgroundColor: "#EEF0FF",
                      borderLeft: "3px solid #1718FF",
                      borderRadius: "4px",
                      px: 1.5,
                      py: 1,
                      my: 1.5,
                      mx: 0,
                      "& p": {
                        color: "#1718FF",
                        fontWeight: 400,
                        fontSize: "0.8rem",
                      },
                    },
                  }}
                >
                  {Array.isArray(caseInfoContent) ? (
                    <span>
                      {caseInfoContent.map((info, i) => (
                        <p key={i}>{info}</p>
                      ))}
                    </span>
                  ) : (
                    <Markdown>{caseInfoContent}</Markdown>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}
    </div>
  );
};

export default SlideViewer;
